import type {
  QueriesGetLeftNavMenuResponse,
  TransferObjectsGetAvailableReportResponse,
  TransferObjectsGetReportGroupsResponseDTO,
  TransferObjectsGetReportsResponseDTO,
} from 'client'
import type { Component, Ref } from 'vue'

export type TPixelSizeString = `${number}px`

export interface ErrorObject {
  message: string
  errors?: string[]
}

interface IQuasarInputRef extends Ref {
  hasError: boolean
}

export type QuasarInputRef = IQuasarInputRef | null

export interface ILeftRightBottomTextParams {
  options: ILeftRightOption[]
  data: Record<string, any[]>
  items: ILeftRightItem[]
  /** in case the bottom text is dynamic, pass the label from the dynamic left right column */
  label?: string
  /** in case the bottom text is dynamic, pass the filteredOptions from the dynamic left right column */
  filteredOptions?: Record<string, ILeftRightOption[]>
}

export type TLeftRightBottomText =
  | string
  | ((params: ILeftRightBottomTextParams) => string)

export const OPasswordValidatorMessages = {
  CONTAINS_EIGHT_CHARS: 'Contains at least eight characters',
  CONTAINS_SPECIAL_CHAR: 'Contains at least one special character',
  CONTAINS_LOWERCASE: 'Contains at least one lowercase letter',
  CONTAINS_UPPERCASE: 'Contains at least one uppercase letter',
  CONTAINS_NUMBER: 'Contains at least one number',
} as const

export type TPasswordValidatorMessage =
  (typeof OPasswordValidatorMessages)[keyof typeof OPasswordValidatorMessages]

export interface IBreadcrumb {
  label: string
  to?: string
  active?: boolean
}

export interface ICropperZoomEvent extends Event {
  detail: { ratio: number; oldRatio: number; originalEvent: any }
}

export const OUserFormStepNames = {
  BASIC_INFORMATION: 'Basic Information',
  SERVICE_LINES: 'Service Lines',
  SERVICE_LEVEL_PERMISSIONS: 'Service Level Permissions',
  PLANS_ANALYSES: 'Plans / Analyses',
  SUMMARY: 'Summary',
} as const
export type TUserFormStep =
  (typeof OUserFormStepNames)[keyof typeof OUserFormStepNames]

export const OReportsTableCheckLabels = {
  ALL: 'ALL',
  NONE: 'NONE',
  PRIMARY: 'PRIMARY',
  SUPPORT: 'SUPPORT',
  PRIMARYANDSUPPORT: 'PRIMARY & SUPPORT',
} as const

export type TReportTableCheckTriggerLabel =
  (typeof OReportsTableCheckLabels)[keyof typeof OReportsTableCheckLabels]
export interface TUserFormStepItem {
  title: TUserFormStep
  component: Component
  visible?: boolean | (() => boolean)
  disabled?: () => boolean
}

export interface ILeftRightOption {
  label: string
  value: string | number
  checked?: boolean
}
export interface ILeftRightItem {
  label: string
  options: ILeftRightOption[]
  bottomText?: TLeftRightBottomText
}

export const OLeftNavigationTitles = {
  DATA_VISUALIZATIONS: 'DATA VISUALIZATIONS',
  INSIGHTS: 'INSIGHTS',
  REPORTS: 'REPORTS',
  SERVICE_DELIVERABLES: 'SERVICE DELIVERABLES',
  COMMUNICATIONS: 'COMMUNICATIONS',
  GOOD_FAITH_EFFORTS: 'GOOD FAITH EFFORTS',
  TOOLS: 'TOOLS',
} as const
export type TLeftNavigationTitle =
  (typeof OLeftNavigationTitles)[keyof typeof OLeftNavigationTitles]

// Just a consistent way to type children

export interface ILeftNavigationItemIcon {
  name: string
  size?: string
  class?: string
}
export interface ILeftNavigationChild {
  label: string
  id: string | number
  to: string
  icon?: ILeftNavigationItemIcon
}

export interface ILeftNavigationItem {
  icon: ILeftNavigationItemIcon
  label: TLeftNavigationTitle
  children?: ILeftNavigationChild[]
  value?: keyof QueriesGetLeftNavMenuResponse
  enumKey?: string
}

export interface ILandingPageData {
  to: string
  parentName: string
}

export type TTableRequestType = 'PUT' | 'PATCH' | 'PARTIAL'

export interface ICheckableReportGroups
  extends TransferObjectsGetReportsResponseDTO {
  checked: boolean
}
export interface IReportsWithCheckableReportGroups
  extends TransferObjectsGetReportGroupsResponseDTO {
  reports: ICheckableReportGroups[]
}
export interface IAvailableReport
  extends TransferObjectsGetAvailableReportResponse {
  isEditPermissionsDialogOpen: boolean
}

export interface IReportGroups
  extends TransferObjectsGetReportGroupsResponseDTO {
  isExpanded: boolean
}

export interface ILabelValue {
  label: string
  value: string | number
}
